import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Note } from '@/contexts/NotesManager';
import { useNotesManager } from '@/contexts/NotesManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Star, 
  Paperclip, 
  Calendar,
  FileText,
  Plus,
  MoreHorizontal,
  Trash2,
  Edit,
  Copy,
  FolderPlus,
  Tag,
  Share,
  Download,
  StarOff
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface NotesListPanelProps {
  notes: Note[];
  selectedNoteId?: string;
  onSelectNote: (noteId: string) => void;
  onNewNote: () => void;
  viewTitle: string;
  searchQuery: string;
  className?: string;
}

export function NotesListPanel({
  notes,
  selectedNoteId,
  onSelectNote,
  onNewNote,
  viewTitle,
  searchQuery,
  className
}: NotesListPanelProps) {
  const { updateNote, deleteNote, addNote, folders, moveNoteToFolder } = useNotesManager();
  const [renamingNoteId, setRenamingNoteId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');

  const getPreviewText = (content: string) => {
    return content
      .replace(/<[^>]+>/g, '') // strip HTML tags
      .replace(/^#+\s*/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .split('\n')
      .find(line => line.trim().length > 0)
      ?.slice(0, 100) || '';
  };

  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<mark class="bg-primary/20 text-primary">$1</mark>');
  };

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, noteId: string) => {
    e.dataTransfer.setData('text/plain', noteId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '0.5';
  };

  // Handle drag end
  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
  };

  // Note actions
  const handleRenameNote = (note: Note) => {
    setRenamingNoteId(note.id);
    setNewTitle(note.title);
  };

  const handleSaveRename = (noteId: string) => {
    if (newTitle.trim()) {
      updateNote(noteId, { title: newTitle.trim() });
    }
    setRenamingNoteId(null);
    setNewTitle('');
  };

  const handleCancelRename = () => {
    setRenamingNoteId(null);
    setNewTitle('');
  };

  const handleDuplicateNote = (note: Note) => {
    const duplicatedNote = {
      title: `${note.title} (Copy)`,
      content: note.content,
      subtitle: note.subtitle,
      folderId: note.folderId,
      tags: [...note.tags],
      attachments: [...note.attachments],
      isFavorite: false,
    };
    addNote(duplicatedNote);
  };

  const handleToggleFavorite = (note: Note) => {
    updateNote(note.id, { isFavorite: !note.isFavorite });
  };

  const handleMoveToFolder = (noteId: string, folderId: string | null) => {
    updateNote(noteId, { folderId: folderId || undefined });
  };

  const handleExportNote = (note: Note) => {
    const content = `# ${note.title}\n\n${note.content}`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${note.title}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShareNote = (note: Note) => {
    if (navigator.share) {
      navigator.share({
        title: note.title,
        text: note.content,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${note.title}\n\n${note.content}`);
      // You might want to show a toast notification here
    }
  };

  return (
    <div className={cn("h-full flex flex-col", className)}>
        {/* Panel Header */}
        <div className="p-4 border-b border-border/20 bg-background/50 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-foreground">{viewTitle}</h2>
            <Button
              onClick={onNewNote}
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {notes.length} {notes.length === 1 ? 'note' : 'notes'}
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto">
        {notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <FileText className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-base font-medium text-foreground mb-2">
              {searchQuery ? 'No matching notes' : 'No notes here'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Create your first note to get started'
              }
            </p>
            {!searchQuery && (
              <Button onClick={onNewNote} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                New Note
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border/10">
            {notes.map((note) => {
              const isSelected = selectedNoteId === note.id;
              const previewText = getPreviewText(note.content);
              
              return (
                <div
                  key={note.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, note.id)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onSelectNote(note.id)}
                  className={cn(
                    "p-4 cursor-pointer transition-all hover:bg-muted/50 relative group border-l-2 border-transparent",
                    isSelected && "bg-primary/10 border-l-primary"
                  )}
                >
                  {/* Note Title */}
                  <div className="flex items-start justify-between mb-2">
                    {renamingNoteId === note.id ? (
                      <input
                        type="text"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveRename(note.id);
                          } else if (e.key === 'Escape') {
                            handleCancelRename();
                          }
                        }}
                        onBlur={() => handleSaveRename(note.id)}
                        className="flex-1 bg-transparent border border-primary/50 rounded px-2 py-1 text-sm font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <h3 
                        className={cn(
                          "font-medium text-sm line-clamp-1 pr-2 text-foreground",
                          isSelected && "text-primary"
                        )}
                        dangerouslySetInnerHTML={{
                          __html: highlightSearchTerm(note.title, searchQuery)
                        }}
                      />
                    )}
                    
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {note.isFavorite && (
                        <Star className="w-3 h-3 text-warning fill-current" />
                      )}
                      
                      {/* Action Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-6 h-6 p-0 hover:bg-muted text-muted-foreground hover:text-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent 
                          className="w-56 bg-background/95 backdrop-blur-xl border border-border/50 shadow-xl z-[9999]"
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem 
                            className="flex items-center gap-2 hover:bg-muted/50"
                            onClick={() => handleRenameNote(note)}
                          >
                            <Edit className="w-4 h-4" />
                            Rename
                          </DropdownMenuItem>
                          
                          <DropdownMenuItem 
                            className="flex items-center gap-2 hover:bg-muted/50"
                            onClick={() => handleDuplicateNote(note)}
                          >
                            <Copy className="w-4 h-4" />
                            Duplicate
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem 
                            className="flex items-center gap-2 hover:bg-muted/50"
                            onClick={() => handleToggleFavorite(note)}
                          >
                            {note.isFavorite ? (
                              <>
                                <StarOff className="w-4 h-4" />
                                Remove from Favorites
                              </>
                            ) : (
                              <>
                                <Star className="w-4 h-4" />
                                Add to Favorites
                              </>
                            )}
                          </DropdownMenuItem>

                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="flex items-center gap-2 hover:bg-muted/50">
                              <FolderPlus className="w-4 h-4" />
                              Move to Folder
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent 
                              className="bg-background/95 backdrop-blur-xl border border-border/50 shadow-xl z-[9999]"
                              sideOffset={5}
                              alignOffset={-5}
                            >
                              <DropdownMenuItem 
                                className="flex items-center gap-2 hover:bg-muted/50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveToFolder(note.id, null);
                                }}
                              >
                                <FileText className="w-4 h-4" />
                                No Folder
                              </DropdownMenuItem>
                              {folders.map((folder) => (
                                <DropdownMenuItem
                                  key={folder.id}
                                  className="flex items-center gap-2 hover:bg-muted/50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveToFolder(note.id, folder.id);
                                  }}
                                >
                                  <div 
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: folder.color }}
                                  />
                                  {folder.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem 
                            className="flex items-center gap-2 hover:bg-muted/50"
                            onClick={() => handleExportNote(note)}
                          >
                            <Download className="w-4 h-4" />
                            Export as Markdown
                          </DropdownMenuItem>

                          <DropdownMenuItem 
                            className="flex items-center gap-2 hover:bg-muted/50"
                            onClick={() => handleShareNote(note)}
                          >
                            <Share className="w-4 h-4" />
                            Share
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem 
                            className="flex items-center gap-2 hover:bg-destructive/20 text-destructive"
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this note?')) {
                                deleteNote(note.id);
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Preview Text */}
                  <p 
                    className="text-sm text-muted-foreground line-clamp-2 mb-3 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: highlightSearchTerm(previewText, searchQuery)
                    }}
                  />

                  {/* Metadata */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Last Updated */}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDistanceToNow(note.updatedAt, { addSuffix: true })}</span>
                      </div>
                      
                      {/* Attachments */}
                      {note.attachments.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Paperclip className="w-3 h-3" />
                          <span>{note.attachments.length}</span>
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    <div className="flex items-center gap-1">
                      {note.tags.slice(0, 2).map((tag) => (
                        <Badge 
                          key={tag} 
                          variant="secondary" 
                          className="text-xs px-1.5 py-0 h-5 bg-muted/50 text-muted-foreground"
                        >
                          {tag}
                        </Badge>
                      ))}
                      {note.tags.length > 2 && (
                        <span className="text-xs text-muted-foreground">
                          +{note.tags.length - 2}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Drag Handle Indicator */}
                  <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-50 text-muted-foreground">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 5h2v2H9V5zm0 6h2v2H9v-2zm0 6h2v2H9v-2zm6-12h2v2h-2V5zm0 6h2v2h-2v-2zm0 6h2v2h-2v-2z"/>
                    </svg>
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}